import {
  Checkbox,
  FieldLabel,
  getFontCss,
  styleConstants,
  Tag,
  TagSizes,
  themeConstants,
} from '@paywithextend/components'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  selectBookkeeperSelectiveAccess,
  selectFilteredCreditCardOptionsWithoutOwn,
  selectUserAccessCreditCards,
  selectUserCreditCardAccessOptions,
  selectUsersOwnAccounts,
  selectusersOwnFilteredCreditCardOptions,
} from 'src/app/PeopleDetailFlyout/redux/peopleDetailFlyout.selectors'
import Box from 'src/components/Box/Box'
import RadioItems from 'src/components/RadioItems/RadioItems'
import SvgGreyLock from 'src/components/Svgs/SvgGreyLock'
import Tooltip from 'src/components/Tooltip/Tooltip'
import { BookkeeperAccountVisibilityTypes, OrganizationRoles } from 'src/enums'
import Trns, { useTranslate } from 'src/localization/Trns'
import { CreditCardListModel } from 'src/models/creditCard.models'
import { UserListModel } from 'src/models/user.models'
import styled, { css, CSSProp } from 'styled-components'

export type AccountAccessOptionsFields = {
  grantFullAccess: boolean
  selectiveAccessAccountIds?: string[]
}
interface AccountPreviewItemProps {
  user?: UserListModel
  cc: CreditCardListModel
  isSelected?: boolean
  selectAccount?: (cc: CreditCardListModel) => void
  deselectAccount?: (cc: CreditCardListModel) => void
  readOnly?: boolean
  isUsersOwnAccount?: boolean
  isLastOwnAccount?: boolean
}

export const AccountPreviewItem: React.FC<AccountPreviewItemProps> = (
  props,
): React.ReactElement => {
  const { user, cc } = props
  const [isSelected, setIsSelected] = useState<boolean>(
    props.isSelected ?? false,
  )
  const selectAccount = (): void => {
    if (isSelected && props.deselectAccount !== undefined) {
      props.deselectAccount(cc)
    } else {
      if (props.selectAccount !== undefined) {
        props.selectAccount(cc)
      }
    }
    setIsSelected(!isSelected)
  }
  return (
    <div
      css={css`
        display: flex;
        justify-content: space-between;
        align-items: center;
        align-self: stretch;
        ${props.isUsersOwnAccount &&
        css`
          opacity: 0.5;
          cursor-events: none;
        `}
        ${props.isLastOwnAccount &&
        css`
          border-bottom: 1px solid ${styleConstants.colors.gray_dark};
        `}
      ${!props.readOnly &&
        css`
          margin: 0px 20px;
        `}
      `}
    >
      <div
        css={css`
          display: flex;
          flex-direction: row;
          align-items: flex-start;
          ${props.readOnly
            ? css`
                margin: 12px 20px;
              `
            : css`
                &:nth-child(-n + 1) {
                  margin-top: 20px;
                }
              `};
          ${props.isUsersOwnAccount &&
          css`
            pointer-events: none;
          `}
        `}
      >
        {!props.readOnly && (
          <Checkbox
            css={css`
              ${props.isUsersOwnAccount &&
              css`
                opacity: 0.5;
              `}
            `}
            isSelected={isSelected}
            onClick={selectAccount}
            isGreyedOut={props.isUsersOwnAccount}
          />
        )}
        <div
          css={css`
            display: flex;
            flex-direction: column;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: row;
              align-items: flex-start;
            `}
          >
            <p
              css={css`
                ${getFontCss(
                  12,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )};
                padding-bottom: 4px;
                margin-right: 8px;
              `}
            >
              {cc.displayName}
            </p>
            <Tag size={TagSizes.XSmall} text={cc.last4} />
          </div>
          <p
            css={css`
              ${getFontCss(
                12,
                styleConstants.colors.gray_darkest,
                styleConstants.fontFamilies.regular,
              )};
              padding-bottom: 16px;
            `}
          >
            {user?.fullNameOrEmail}
          </p>
        </div>
      </div>
      <div
        css={css`
          margin-bottom: 32px;
          ${!props.readOnly
            ? css`
                margin-top: 20px;
              `
            : css`
                margin-right: 20px;
              `}
        `}
      >
        {props.isUsersOwnAccount && <SvgGreyLock />}
      </div>
    </div>
  )
}

interface BookkeeperRadioItemProps {
  selectedIndex: number | undefined
  highlightIndex: number
  description: React.ReactElement
  hoveredIndex?: number
  'data-testid'?: string
}

export const BookkeeperAccountRadioItem: React.FC<BookkeeperRadioItemProps> = (
  props,
): React.ReactElement => {
  return (
    <span data-testid={props['data-testid']}>
      <p
        css={css`
          ${getFontCss(
            12,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.regular,
          )};
        `}
      >
        {props.description}
      </p>
    </span>
  )
}

interface AccountAccessOptionsProps {
  // will implement readOnly later when we have designs for all cases
  readOnly?: boolean
  showHeader?: boolean
  updateAccess: (fields: AccountAccessOptionsFields) => void
  useDefaults?: boolean
  person?: UserListModel
  userRole?: OrganizationRoles
  setSelectedAccountsList?: (accounts: CreditCardListModel[]) => void
  selectedRole: OrganizationRoles
}

const StyledDiv = styled.div<{
  showHeader?: boolean
}>`
  display: flex;
  flex-direction: column;
  overflow: visible;
  ${({ showHeader }): CSSProp => {
    return showHeader
      ? css`
          border-top: 1px solid ${styleConstants.colors.gray_light};
        `
      : css``
  }}
`

const AccountAccessOptions: React.FC<AccountAccessOptionsProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()
  const usersOwnCreditCardOptions = useSelector(
    selectusersOwnFilteredCreditCardOptions,
  )
  const creditCardOptionsWithoutUsersOwn = useSelector(
    selectFilteredCreditCardOptionsWithoutOwn,
  )
  const filteredCreditCardOptions = [
    ...usersOwnCreditCardOptions,
    ...creditCardOptionsWithoutUsersOwn,
  ]
  const userCreditCardAccessOptions = props.useDefaults
    ? []
    : useSelector(selectUserCreditCardAccessOptions)
  const userAccessCreditCards = useSelector(selectUserAccessCreditCards)
  const bookkeeperAccessLevel = useSelector(selectBookkeeperSelectiveAccess)
  const personsOwnAccounts = useSelector(selectUsersOwnAccounts)
  const [
    selectedIndexForBookkeeperRadioItems,
    setSelectedIndexForBookkeeperRadioItems,
  ] = useState<number | undefined>(
    bookkeeperAccessLevel === BookkeeperAccountVisibilityTypes.ALL ||
      bookkeeperAccessLevel === undefined ||
      props.useDefaults
      ? 0
      : 1,
  )
  const [showAccountsBox, setShowAccountsBox] = useState<boolean | undefined>(
    selectedIndexForBookkeeperRadioItems === 1 ||
      props.userRole === OrganizationRoles.cardManager,
  )
  const [selectedAccounts, setSelectedAccounts] = useState<
    CreditCardListModel[]
  >(userAccessCreditCards !== undefined ? userAccessCreditCards : [])

  const canUserUpdateAccess =
    props.selectedRole === OrganizationRoles.bookkeeper
      ? props.userRole === OrganizationRoles.admin ||
        props.userRole === OrganizationRoles.owner
      : props.userRole === OrganizationRoles.owner

  const filterCardManagersOwnAccounts = (
    allAccounts: CreditCardListModel[],
    ownAccounts: CreditCardListModel[],
  ): CreditCardListModel[] => {
    return allAccounts.filter(
      (acc) => !ownAccounts.some((ownAcc) => ownAcc?.id === acc?.id),
    )
  }

  useEffect(() => {
    if (props.setSelectedAccountsList && userAccessCreditCards !== undefined)
      props.setSelectedAccountsList(userAccessCreditCards)
  }, [])

  useEffect(() => {
    // checking if the index is 1 because we only want to show the accounts box when the second radio button is selected
    setShowAccountsBox(selectedIndexForBookkeeperRadioItems === 1)
    const allSelectedAccounts = [
      ...personsOwnAccounts,
      ...selectedAccounts,
      ...(userAccessCreditCards !== undefined ? userAccessCreditCards : []),
    ]
    const requestAccountIds =
      props.selectedRole === OrganizationRoles.bookkeeper
        ? allSelectedAccounts
        : props.selectedRole === OrganizationRoles.cardManager
          ? filterCardManagersOwnAccounts(
              allSelectedAccounts,
              personsOwnAccounts,
            )
          : []
    props.updateAccess({
      grantFullAccess:
        selectedIndexForBookkeeperRadioItems === 0 &&
        !!(props.selectedRole === OrganizationRoles.bookkeeper),
      selectiveAccessAccountIds: requestAccountIds
        .map((selectedAccount) => selectedAccount?.id)
        .filter((acc) => acc !== undefined) as string[],
    })
  }, [selectedIndexForBookkeeperRadioItems])

  useEffect(() => {
    switch (props.selectedRole) {
      case OrganizationRoles.cardManager:
        setShowAccountsBox(true)
        break
      case OrganizationRoles.bookkeeper:
        if (selectedIndexForBookkeeperRadioItems === 1) setShowAccountsBox(true)
        break
      default:
        setShowAccountsBox(false)
        break
    }
  }, [props.userRole])

  const selectAccount = (cc: CreditCardListModel): void => {
    const allSelectedAccounts = [...personsOwnAccounts, ...selectedAccounts, cc]
    const requestAccountIds =
      props.selectedRole === OrganizationRoles.bookkeeper
        ? allSelectedAccounts
        : props.selectedRole === OrganizationRoles.cardManager
          ? filterCardManagersOwnAccounts(
              allSelectedAccounts,
              personsOwnAccounts,
            )
          : []
    setSelectedAccounts(requestAccountIds)
    props.setSelectedAccountsList &&
      props.setSelectedAccountsList([...new Set(allSelectedAccounts)])
    props.updateAccess({
      grantFullAccess: !showAccountsBox,
      selectiveAccessAccountIds: !showAccountsBox
        ? []
        : requestAccountIds.map((selectedAccount) => selectedAccount.id),
    })
  }

  const deselectAccount = (cc: CreditCardListModel): void => {
    const updatedArray = selectedAccounts.filter(
      (selectedAccount) => selectedAccount.id !== cc.id,
    )
    setSelectedAccounts(updatedArray)
    props.setSelectedAccountsList &&
      props.setSelectedAccountsList([...updatedArray])
    props.updateAccess({
      grantFullAccess: !showAccountsBox,
      selectiveAccessAccountIds: !showAccountsBox
        ? []
        : updatedArray.map((selectedAccount) => selectedAccount.id),
    })
  }

  const title = (): string => {
    switch (props.selectedRole) {
      case OrganizationRoles.bookkeeper:
        return 'Account Access'
      case OrganizationRoles.cardManager:
        return 'Account Edit Access'
      default:
        return ''
    }
  }

  const tooltipDescription = (): React.ReactElement => {
    switch (props.selectedRole) {
      case OrganizationRoles.bookkeeper:
        return (
          <Trns
            source={
              'Give Bookkeepers access to view either specific or all accounts in your organization. This lets them see all virtual cards, budgets, and transactions for those accounts. This access can be updated later.'
            }
          />
        )
      case OrganizationRoles.cardManager:
        return (
          <>
            <Trns
              source={
                'Edit access over an account is the ability to create, update, and close virtual cards and budgets on that account.'
              }
            />
            <span
              css={css`
                margin-top: 10px;
              `}
            >
              <Trns
                source={
                  'For accounts registered by this user, edit access is automatically granted and cannot be changed.'
                }
              />
            </span>
          </>
        )
      default:
        return <></>
    }
  }

  const bookkeeperAccountRadioItems = [
    <BookkeeperAccountRadioItem
      selectedIndex={selectedIndexForBookkeeperRadioItems}
      highlightIndex={0}
      description={
        <Trns
          source={
            'Grant access to all current and future accounts registered to this organization'
          }
        />
      }
      key={'all'}
    />,
    <BookkeeperAccountRadioItem
      selectedIndex={selectedIndexForBookkeeperRadioItems}
      highlightIndex={1}
      description={<Trns source={'Only allow access to specific accounts'} />}
      key={'specific'}
      data-testid="bookkeeper-access-selective"
    />,
  ]
  return (
    <StyledDiv showHeader={props.showHeader}>
      {props.showHeader && (
        <>
          <FieldLabel
            css={css`
              margin-top: 20px;
              display: flex;
              overflow: visible;
            `}
          >
            <span
              css={css`
                ${getFontCss(
                  16,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.medium,
                )};
                margin-top: 2px;
              `}
            >
              <Trns source={title()} />
            </span>
            <Tooltip openLeft={false}>{tooltipDescription()}</Tooltip>
          </FieldLabel>
          {props.selectedRole === OrganizationRoles.cardManager && (
            <span
              css={css`
                ${getFontCss(
                  12,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )}
              `}
            >
              <Trns
                source={
                  'Allowed edit access to the below accounts. This access can only be updated by the Owner.'
                }
              />
            </span>
          )}
        </>
      )}
      {props.selectedRole === OrganizationRoles.bookkeeper && (
        <RadioItems
          items={bookkeeperAccountRadioItems}
          currentIndex={selectedIndexForBookkeeperRadioItems}
          setIndex={setSelectedIndexForBookkeeperRadioItems}
          noBorder
          centered
          ariaLabels={[
            translate(
              'Grant access to all current and future accounts registered to this organization',
            ),
            translate('Only allow access to specific accounts'),
          ]}
          css={css`
            ${!props.showHeader &&
            css`
              margin-top: 20px;
            `}
          `}
        />
      )}
      {showAccountsBox &&
        filteredCreditCardOptions !== undefined &&
        (canUserUpdateAccess ? (
          <Box
            noPadding
            css={css`
              margin-top: 20px;
              background-color: ${styleConstants.colors.gray_lightest};
              box-shadow: none;
              max-height: 240px;
              overflow: scroll;
              border: 1px solid ${styleConstants.colors.gray_medium};
            `}
          >
            {usersOwnCreditCardOptions !== undefined &&
              usersOwnCreditCardOptions.map((opt, i) => {
                const user =
                  opt.details?.creditCard?.user !== undefined
                    ? new UserListModel(opt.details?.creditCard?.user)
                    : undefined
                const isLastOwnAccount =
                  i === usersOwnCreditCardOptions.length - 1
                return (
                  opt.details?.creditCard !== undefined && (
                    <AccountPreviewItem
                      key={i}
                      user={user}
                      cc={opt.details?.creditCard}
                      selectAccount={selectAccount}
                      deselectAccount={deselectAccount}
                      isSelected
                      isUsersOwnAccount
                      isLastOwnAccount={isLastOwnAccount}
                    />
                  )
                )
              })}
            {creditCardOptionsWithoutUsersOwn !== undefined &&
              creditCardOptionsWithoutUsersOwn.map((opt, i) => {
                const user =
                  opt.details?.creditCard?.user !== undefined
                    ? new UserListModel(opt.details?.creditCard?.user)
                    : undefined
                const isAccountSelected =
                  opt.details?.creditCard?.id !== undefined &&
                  userCreditCardAccessOptions !== undefined &&
                  userCreditCardAccessOptions.some(
                    (acc) =>
                      acc.details?.creditCard?.id ===
                      opt.details?.creditCard?.id,
                  )
                return (
                  opt.details?.creditCard !== undefined && (
                    <AccountPreviewItem
                      key={i}
                      user={user}
                      cc={opt.details?.creditCard}
                      selectAccount={selectAccount}
                      deselectAccount={deselectAccount}
                      isSelected={isAccountSelected}
                    />
                  )
                )
              })}
          </Box>
        ) : (
          <div
            css={css`
              margin-top: 20px;
              max-height: 240px;
              overflow: scroll;
            `}
          >
            {filteredCreditCardOptions.map((opt) => {
              const user =
                opt.details?.creditCard?.user !== undefined
                  ? new UserListModel(opt.details?.creditCard?.user)
                  : undefined
              const isUsersOwnAccount =
                opt.details?.creditCard?.ownerId === props.person?.id
              return (
                opt.details?.creditCard !== undefined && (
                  <Box
                    noPadding
                    key={opt.details?.creditCard.id}
                    css={css`
                      background-color: ${styleConstants.colors.gray_lightest};
                      box-shadow: none;
                      max-height: 60px;
                      border: 1px solid ${styleConstants.colors.gray_medium};
                      margin-top: 12px;
                    `}
                  >
                    <AccountPreviewItem
                      user={user}
                      cc={opt.details?.creditCard}
                      isUsersOwnAccount={isUsersOwnAccount}
                      readOnly
                    />
                  </Box>
                )
              )
            })}
          </div>
        ))}
    </StyledDiv>
  )
}

export default AccountAccessOptions
